import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthUser, LoginRequest, LoginResponse } from '../types';
import { authService } from '../services/api';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  setUserOrganization: (orgId: string, orgName: string) => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const AUTH_STORAGE_KEY = 'powersite_auth';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing authentication on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedAuth = localStorage.getItem(AUTH_STORAGE_KEY);
        if (storedAuth) {
          const authData = JSON.parse(storedAuth);
          
          // Check if token is still valid (not expired)
          const now = Date.now() / 1000;
          const tokenExpiry = authData.issuedAt + authData.expiresIn;
          
          if (tokenExpiry > now) {
            setUser(authData);
          } else {
            // Token expired, clear storage and require re-login
            localStorage.removeItem(AUTH_STORAGE_KEY);
          }
        }
      } catch (error) {
        localStorage.removeItem(AUTH_STORAGE_KEY);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const transformLoginResponse = (response: LoginResponse): AuthUser => {
    return {
      id: response.user_id,
      email: response.user_email,
      accessToken: response.access_token,
      refreshToken: response.refresh_token,
      tokenType: response.token_type || 'Bearer',
      expiresIn: response.expires_in,
      issuedAt: response.issued_at,
    };
  };

  const login = async (credentials: LoginRequest): Promise<{ success: boolean; message?: string }> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await authService.login(credentials);
      
      if (response.success && response.data) {
        const authData = transformLoginResponse(response.data);
        
        // Note: Organization data will be fetched separately during organization selection
        // This allows users to choose which organization to access
        
        setUser(authData);
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
        return { success: true };
      } else {
        const errorMessage = response.message || 'Login failed. Please check your credentials.';
        setError(errorMessage);
        return { success: false, message: errorMessage };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred during login.';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const setUserOrganization = (orgId: string, orgName: string) => {
    if (user) {
      const updatedUser = { ...user, orgId, orgName };
      setUser(updatedUser);
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(updatedUser));
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Silently handle logout errors
    } finally {
      setUser(null);
      setError(null);
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    setUserOrganization,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
